import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react';
import { AppState, type AppStateStatus } from 'react-native';

import {
  fetchPortfolioSnapshots,
  replacePortfolioAccounts,
  type SyncedPortfolioAccount,
  type SyncedPortfolioSnapshot,
} from '../services/portfolioSyncService';
import { todayAsOfDate } from '../utils/balanceObservations';
import {
  isPortfolioInvestmentAccount,
  isSpendLikePortfolioAccount,
} from '../utils/portfolioAccountCategory';
import { createPortfolioAccountId } from '../utils/portfolioAccountId';
import { isOwnPortfolioAccount } from '../utils/portfolioOwnership';
import { navigateToAuth } from '../core/navigation/navigationRef';
import { ApiError } from '../services/apiClient';
import {
  notifyDashboardRefresh,
  subscribeDashboardRefresh,
} from '../services/dashboardRefreshBus';
import {
  insightRefreshKey,
  requestInsightsRefresh,
} from '../services/insightRefreshGate';
import { useAuth } from './AuthContext';
import { useHouseholdMembership } from './HouseholdMembershipContext';
import { useSampleData } from './SampleDataContext';

export type PortfolioAccountEdit = {
  label: string;
  balance: number;
  expectedReturnPct?: number;
  /** Active investment (default) vs Other assets. */
  isInvestment?: boolean;
  /** Hide from partner when household view is Combined. */
  hiddenFromHousehold?: boolean;
};

export type PortfolioAddOptions = {
  /** false → categorize under Other assets. Default true. */
  isInvestment?: boolean;
};

type PortfolioContextValue = {
  loading: boolean;
  mutating: boolean;
  error: string | null;
  /** Newest → oldest upload days from Mongo. */
  snapshots: SyncedPortfolioSnapshot[];
  latest: SyncedPortfolioSnapshot | null;
  /** Exact account rows from the latest upload. */
  accounts: SyncedPortfolioAccount[];
  totalBalance: number;
  refresh: () => Promise<void>;
  /** Apply a just-saved snapshot without waiting for a refetch. */
  applySnapshot: (snapshot: SyncedPortfolioSnapshot) => void;
  updateAccount: (index: number, edit: PortfolioAccountEdit) => Promise<void>;
  deleteAccount: (index: number) => Promise<void>;
  /** Manually add an account (synced; same Trends identity on later updates). */
  addAccount: (edit: PortfolioAccountEdit) => Promise<void>;
  /**
   * Ask a mounted portfolio list to open the Add account sheet
   * (e.g. Net worth → Other assets with `{ isInvestment: false }`).
   */
  openAddAccount: (options?: PortfolioAddOptions) => void;
  /** Increments when openAddAccount is called — editors subscribe. */
  addAccountRequestId: number;
  /** Category for the pending add sheet request. */
  pendingAddIsInvestment: boolean;
};

const PortfolioContext = createContext<PortfolioContextValue | null>(null);

function withStableIds(accounts: SyncedPortfolioAccount[]): SyncedPortfolioAccount[] {
  return accounts.map((account) => {
    const isInvestment = isPortfolioInvestmentAccount(account);
    return {
      ...account,
      accountId: account.accountId?.trim() || createPortfolioAccountId(),
      isInvestment,
      type: account.type || (isInvestment ? 'investment' : 'other'),
    };
  });
}

export function PortfolioProvider({ children }: PropsWithChildren) {
  const { isAuthenticated, tokens, user } = useAuth();
  const { isSampleMode } = useSampleData();
  const { view: householdView, viewReady } = useHouseholdMembership();
  const [loading, setLoading] = useState(false);
  const [mutating, setMutating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snapshots, setSnapshots] = useState<SyncedPortfolioSnapshot[]>([]);
  const [latestFromApi, setLatestFromApi] = useState<SyncedPortfolioSnapshot | null>(null);
  const [addAccountRequestId, setAddAccountRequestId] = useState(0);
  const [pendingAddIsInvestment, setPendingAddIsInvestment] = useState(true);
  const snapshotsRef = useRef(snapshots);
  snapshotsRef.current = snapshots;
  const latestRef = useRef<SyncedPortfolioSnapshot | null>(null);
  const refreshInFlight = useRef<Promise<void> | null>(null);
  const userId = user?.id ?? null;

  const refresh = useCallback(async () => {
    if (!isAuthenticated || isSampleMode || !tokens?.accessToken) {
      setSnapshots([]);
      setLatestFromApi(null);
      setError(null);
      return;
    }
    if (!viewReady) return;
    if (refreshInFlight.current) return refreshInFlight.current;

    setLoading(true);
    setError(null);
    const run = (async () => {
      try {
        const load = () => fetchPortfolioSnapshots(30, householdView);
        let data: Awaited<ReturnType<typeof load>>;
        try {
          data = await load();
        } catch (err) {
          const status = err instanceof ApiError ? err.statusCode : undefined;
          const retryable = status == null || status >= 500;
          if (!retryable) throw err;
          await new Promise((resolve) => setTimeout(resolve, 400));
          data = await load();
        }
        setSnapshots(data.snapshots ?? []);
        setLatestFromApi(data.latest ?? null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load portfolio');
      } finally {
        setLoading(false);
        refreshInFlight.current = null;
      }
    })();
    refreshInFlight.current = run;
    return run;
  }, [isAuthenticated, isSampleMode, tokens?.accessToken, householdView, viewReady]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    return subscribeDashboardRefresh(() => {
      void refresh();
    });
  }, [refresh]);

  // Always re-fetch from network when the app returns to foreground.
  useEffect(() => {
    const onChange = (status: AppStateStatus) => {
      if (status === 'active') {
        void refresh();
      }
    };
    const sub = AppState.addEventListener('change', onChange);
    return () => sub.remove();
  }, [refresh]);

  const applySnapshot = useCallback((snapshot: SyncedPortfolioSnapshot) => {
    setSnapshots((prev) => {
      const rest = prev.filter((s) => s.asOfDate !== snapshot.asOfDate);
      return [snapshot, ...rest].sort((a, b) => b.asOfDate.localeCompare(a.asOfDate));
    });
    setLatestFromApi(snapshot);
  }, []);

  /**
   * Persist *this user's* account list to *today*.
   * Partner rows are kept server-side and must not be included in the payload.
   */
  const persistOwnAccounts = useCallback(
    async (nextOwnAccounts: SyncedPortfolioAccount[]) => {
      const current = snapshotsRef.current[0];
      const asOfDate = todayAsOfDate();
      const broker = current?.broker ?? null;
      const ownOnly = withStableIds(nextOwnAccounts)
        .filter((a) => !userId || isOwnPortfolioAccount(a, userId))
        .map((a) => ({
          ...a,
          ownerUserId: userId ?? a.ownerUserId,
        }));
      setMutating(true);
      setError(null);
      try {
        await replacePortfolioAccounts({
          asOfDate,
          broker,
          accounts: ownOnly,
        });
        await refresh();
        // Stored Home AI cards are not live — regenerate after holdings change
        // so the strip cannot keep quoting a deleted account.
        const key = insightRefreshKey(userId ?? undefined, householdView);
        void requestInsightsRefresh(key, 'portfolio_mutate')
          .then(() => {
            notifyDashboardRefresh('portfolio_insights_ready');
          })
          .catch((err) => {
            console.warn('[Portfolio] Insight refresh after mutate failed', err);
          });
        notifyDashboardRefresh('portfolio_mutate');
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not update portfolio');
        throw err;
      } finally {
        setMutating(false);
      }
    },
    [householdView, refresh, userId],
  );

  /** Own rows from the currently displayed (view-filtered) latest list. */
  const cloneOwnAccounts = useCallback((): SyncedPortfolioAccount[] => {
    const displayed = latestRef.current?.accounts ?? [];
    return withStableIds(
      displayed
        .filter((a) => isOwnPortfolioAccount(a, userId))
        .map((a) => ({ ...a })),
    );
  }, [userId]);

  const updateAccount = useCallback(
    async (index: number, edit: PortfolioAccountEdit) => {
      const displayed = latestRef.current?.accounts ?? [];
      const target = displayed[index];
      if (!target || !isOwnPortfolioAccount(target, userId)) {
        setError('You can only edit your own accounts');
        return;
      }
      const own = cloneOwnAccounts();
      const targetId = target.accountId?.trim();
      const ownIndex = own.findIndex(
        (a) =>
          (targetId && a.accountId === targetId) ||
          (!targetId &&
            a.label.trim().toLowerCase() === target.label.trim().toLowerCase() &&
            Math.abs(a.balance - target.balance) < 0.02),
      );
      if (ownIndex < 0) {
        setError('Account not found');
        return;
      }
      const prev = own[ownIndex]!;
      const isInvestment =
        edit.isInvestment != null ? edit.isInvestment : prev.isInvestment !== false;
      own[ownIndex] = {
        ...prev,
        accountId: prev.accountId || createPortfolioAccountId(),
        label: edit.label.trim(),
        balance: Math.max(0, edit.balance),
        expectedReturnPct: isInvestment ? edit.expectedReturnPct : undefined,
        isInvestment,
        type: prev.type || (isInvestment ? 'investment' : 'other'),
        ownerUserId: userId ?? prev.ownerUserId,
        hiddenFromHousehold:
          edit.hiddenFromHousehold != null
            ? edit.hiddenFromHousehold
            : prev.hiddenFromHousehold === true,
      };
      await persistOwnAccounts(own);
    },
    [cloneOwnAccounts, persistOwnAccounts, userId],
  );

  const deleteAccount = useCallback(
    async (index: number) => {
      const displayed = latestRef.current?.accounts ?? [];
      const target = displayed[index];
      if (!target || !isOwnPortfolioAccount(target, userId)) {
        setError('You can only delete your own accounts');
        return;
      }
      const targetId = target.accountId?.trim();
      const own = cloneOwnAccounts().filter((a) => {
        if (targetId && a.accountId) return a.accountId !== targetId;
        return !(
          a.label.trim().toLowerCase() === target.label.trim().toLowerCase() &&
          Math.abs(a.balance - target.balance) < 0.02
        );
      });
      await persistOwnAccounts(own);
    },
    [cloneOwnAccounts, persistOwnAccounts, userId],
  );

  const addAccount = useCallback(
    async (edit: PortfolioAccountEdit) => {
      const trimmed = edit.label.trim();
      if (!trimmed) {
        setError('Enter an account name');
        return;
      }
      const isInvestment = edit.isInvestment !== false;
      const own = cloneOwnAccounts();
      own.push({
        accountId: createPortfolioAccountId(),
        label: trimmed,
        balance: Math.max(0, edit.balance),
        expectedReturnPct: isInvestment ? edit.expectedReturnPct : undefined,
        type: isInvestment ? 'investment' : 'other',
        isInvestment,
        source: 'manual',
        ownerUserId: userId ?? undefined,
      });
      await persistOwnAccounts(own);
    },
    [cloneOwnAccounts, persistOwnAccounts, userId],
  );
  const openAddAccount = useCallback(
    (options?: PortfolioAddOptions) => {
      if (!isAuthenticated) {
        navigateToAuth();
        return;
      }
      setPendingAddIsInvestment(options?.isInvestment !== false);
      setAddAccountRequestId((n) => n + 1);
    },
    [isAuthenticated],
  );

  // Prefer API synthesized `latest` (carry-forward per account). Fallback: first
  // non-empty day so a partner's newer upload doesn't blank Mine portfolio.
  const latest = useMemo(() => {
    const raw =
      latestFromApi && (latestFromApi.accounts?.length ?? 0) > 0
        ? latestFromApi
        : snapshots.find((s) => (s.accounts?.length ?? 0) > 0) ??
          latestFromApi ??
          snapshots[0] ??
          null;
    if (!raw) return null;
    const holdings = (raw.accounts ?? []).filter(
      (account) => !isSpendLikePortfolioAccount(account),
    );
    if (holdings.length === (raw.accounts?.length ?? 0)) return raw;
    const total =
      Math.round(holdings.reduce((sum, account) => sum + (account.balance || 0), 0) * 100) /
      100;
    return { ...raw, accounts: holdings, totalBalance: total };
  }, [latestFromApi, snapshots]);
  latestRef.current = latest;
  const accounts = latest?.accounts ?? [];
  const totalBalance =
    latest?.totalBalance ??
    accounts.reduce((sum, a) => sum + (a.balance || 0), 0);

  const value = useMemo<PortfolioContextValue>(
    () => ({
      loading,
      mutating,
      error,
      snapshots,
      latest,
      accounts,
      totalBalance,
      refresh,
      applySnapshot,
      updateAccount,
      deleteAccount,
      addAccount,
      openAddAccount,
      addAccountRequestId,
      pendingAddIsInvestment,
    }),
    [
      loading,
      mutating,
      error,
      snapshots,
      latest,
      accounts,
      totalBalance,
      refresh,
      applySnapshot,
      updateAccount,
      deleteAccount,
      addAccount,
      openAddAccount,
      addAccountRequestId,
      pendingAddIsInvestment,
    ],
  );

  return (
    <PortfolioContext.Provider value={value}>{children}</PortfolioContext.Provider>
  );
}

export function usePortfolio(): PortfolioContextValue {
  const ctx = useContext(PortfolioContext);
  if (!ctx) {
    throw new Error('usePortfolio must be used within PortfolioProvider');
  }
  return ctx;
}
